/**
 * HMAC-signed offline subscription cache.
 *
 * Threat: a kid (or a curious adult) opens DevTools and edits localStorage
 * to extend their parent's subscription `current_period_end`, unlocking
 * paid features without paying. Pre-HMAC, the cache was plaintext JSON and
 * trivially tamperable.
 *
 * Defense: sign the cache with HMAC-SHA256. The signing key is generated
 * once per device, stored in IndexedDB (not localStorage), and bound to
 * the Supabase user id. So:
 *   - Editing localStorage breaks the signature → cache is dropped.
 *   - Copying a cache from another user fails the userId binding check.
 *   - Wiping IDB is allowed (next sign-in re-fetches from server) but
 *     wiping IDB *and* keeping the LS cache also fails verification.
 *
 * This is NOT a server-side license check — a determined attacker who can
 * pause the JS and patch the verification function still wins. The bar is
 * "casual tampering" not "rooted client". For real enforcement, use the
 * server check (`refreshSubscription` from authStore) which always runs
 * when online.
 */
import { OFFLINE_GRACE_DAYS } from './subscription';
import type { Subscription } from '../types/auth';

const SUB_CACHE_KEY = 'homeschool-sub-cache';
const IDB_NAME = 'homeschool-secure';
const IDB_STORE = 'keys';
const KEY_ID = 'sub-cache-hmac';

interface SignedEnvelope {
  payload: string; // JSON of CachedPayload
  sig: string; // base64 HMAC-SHA256
}

interface CachedPayload {
  subscription: Subscription;
  validUntil: string;
  cachedAt: string;
  userId: string;
}

// --- IndexedDB key storage --------------------------------------------------

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key: string): Promise<ArrayBuffer | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(key);
    req.onsuccess = () => resolve(req.result as ArrayBuffer | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(key: string, value: ArrayBuffer): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// --- HMAC key lifecycle -----------------------------------------------------

let cachedKey: CryptoKey | null = null;

async function getOrCreateKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;

  let raw = await idbGet(KEY_ID);
  if (!raw) {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    await idbPut(KEY_ID, bytes.buffer);
    raw = bytes.buffer;
  }

  cachedKey = await crypto.subtle.importKey(
    'raw',
    raw,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify']
  );
  return cachedKey;
}

function bytesToBase64(bytes: ArrayBuffer): string {
  const view = new Uint8Array(bytes);
  let bin = '';
  for (let i = 0; i < view.length; i++) bin += String.fromCharCode(view[i]);
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function payloadBuf(payload: string): ArrayBuffer {
  // Copy the encoded bytes into a fresh ArrayBuffer so the type is the
  // strict ArrayBuffer that BufferSource expects (not ArrayBufferLike).
  const enc = new TextEncoder().encode(payload);
  const out = new Uint8Array(enc.byteLength);
  out.set(enc);
  return out.buffer;
}

async function sign(payload: string): Promise<string> {
  const key = await getOrCreateKey();
  const sig = await crypto.subtle.sign('HMAC', key, payloadBuf(payload));
  return bytesToBase64(sig);
}

async function verify(payload: string, sig: string): Promise<boolean> {
  try {
    const key = await getOrCreateKey();
    const sigBuf = new Uint8Array(base64ToBytes(sig)).buffer;
    const ok = await crypto.subtle.verify('HMAC', key, sigBuf, payloadBuf(payload));
    return ok;
  } catch {
    return false;
  }
}

// --- Public API -------------------------------------------------------------

export async function cacheSubscription(sub: Subscription, userId: string): Promise<void> {
  const periodEnd = sub.current_period_end ? new Date(sub.current_period_end) : new Date();
  const validUntil = new Date(periodEnd);
  validUntil.setDate(validUntil.getDate() + OFFLINE_GRACE_DAYS);

  const payload: CachedPayload = {
    subscription: sub,
    validUntil: validUntil.toISOString(),
    cachedAt: new Date().toISOString(),
    userId,
  };
  const payloadStr = JSON.stringify(payload);
  const sig = await sign(payloadStr);
  const envelope: SignedEnvelope = { payload: payloadStr, sig };
  try {
    localStorage.setItem(SUB_CACHE_KEY, JSON.stringify(envelope));
  } catch {
    // localStorage full / disabled — silently skip; server check will run next time online.
  }
}

export async function getCachedSubscription(
  userId: string
): Promise<{ subscription: Subscription; expired: boolean } | null> {
  try {
    const raw = localStorage.getItem(SUB_CACHE_KEY);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as SignedEnvelope;
    if (!envelope?.payload || !envelope?.sig) return null;
    const ok = await verify(envelope.payload, envelope.sig);
    if (!ok) {
      // Tampered or signed with a different device key. Drop it.
      localStorage.removeItem(SUB_CACHE_KEY);
      return null;
    }
    const payload = JSON.parse(envelope.payload) as CachedPayload;
    if (payload.userId !== userId) {
      // Cross-account contamination — drop.
      localStorage.removeItem(SUB_CACHE_KEY);
      return null;
    }
    const expired = new Date() > new Date(payload.validUntil);
    return { subscription: payload.subscription, expired };
  } catch {
    return null;
  }
}

export function clearCachedSubscription(): void {
  try {
    localStorage.removeItem(SUB_CACHE_KEY);
  } catch {
    /* localStorage may be disabled in private mode — nothing we can do */
  }
}
